import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';
import './ProjectsDashboard.css';


const ProjectsDashboard = () => {
  const navigate = useNavigate();
  const { showToast, showConfirm } = useToast();
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // API Base URL
  const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';
  const BASE_URL = 'https://eks-projectmanager.eksconstruction.in';

  // Fetch projects from API on component mount
  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Fetching projects from:', `${API_BASE_URL}/get_projects.php`);
      
      const response = await fetch(`${API_BASE_URL}/get_projects.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('API Response:', result);

      if (result.success && result.projects) {
        // Map database field names to component field names
        const mappedProjects = result.projects.map(p => ({
          id: p.id,
          projectType: p.project_type,
          clientName: p.client_name,
          projectTitle: p.project_title,
          location: p.location,
          area: p.area,
          floors: p.floors,
          phase: p.phase,
          startDate: p.start_date,
          completionDate: p.completion_date,
          progress: p.progress,
          keyFeatures: p.key_features,
          images: p.images || [],
          createdAt: p.created_at
        }));
        
        setProjects(mappedProjects);
      } else {
        console.error('API Error:', result.error);
        setProjects([]);
      }
    } catch (error) {
      console.error('Fetch Error:', error);
      try { showToast && showToast({ message: 'Error fetching projects: ' + error.message, type: 'error' }); } catch(e){}
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleAddNew = () => {
    navigate('/');
  };

  const handleGallery = () => {
    navigate('/gallery');
  };

  const handlePackages = () => {
    navigate('/packages');
  };

  const handleViewDetails = (projectId) => {
    navigate(`/project/${projectId}`);
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const confirmed = await showConfirm('Are you sure you want to delete this project?', {
        title: 'Delete Project',
        confirmText: 'Delete',
        cancelText: 'Cancel'
      });

      if (!confirmed) return;

      const response = await fetch(`${API_BASE_URL}/delete_project.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: projectId })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Delete failed');
      }

      // Remove from state
      setProjects(prev => prev.filter(p => p.id !== projectId));
      // Refresh list
      await fetchProjects();
      showToast({ message: 'Project deleted successfully', type: 'success' });
    } catch (error) {
      showToast({ message: 'Failed to delete project: ' + error.message, type: 'error' });
    }
  };

  const getFilteredProjects = () => {
    let filtered = projects;

    // Filter by type
    if (filter !== 'All') {
      filtered = filtered.filter(p => p.projectType === filter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p => 
        (p.projectTitle && p.projectTitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.clientName && p.clientName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.location && p.location.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered;
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'Completed Projects':
        return 'status-completed';
      case 'Ongoing Projects':
        return 'status-ongoing';
      case 'Upcoming Projects':
        return 'status-upcoming';
      default:
        return 'status-ongoing';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const filteredProjects = getFilteredProjects();

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-large">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h1>Projects Dashboard</h1>
              <p className="header-subtitle">Manage and track all your construction projects</p>
            </div>
          </div>
          <div className="header-buttons">
            <button className="add-new-btn" onClick={handleAddNew}>
              <span className="btn-plus">+</span>
              Add New Project
            </button>
            <button className="gallery-nav-btn" onClick={handleGallery}>
              <span className="btn-icon">🖼️</span>
              Gallery
            </button>
            <button className="gallery-nav-btn" onClick={handlePackages}>
              <span className="btn-icon">📦</span>
              Packages
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-container">
        <div className="stat-card stat-all">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>{projects.length}</h3>
            <p>Total Projects</p>
          </div>
        </div>
        <div className="stat-card stat-ongoing">
          <div className="stat-icon">🚧</div>
          <div className="stat-info">
            <h3>{projects.filter(p => p.projectType === 'Ongoing Projects').length}</h3>
            <p>Ongoing</p>
          </div>
        </div>
        <div className="stat-card stat-completed">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <h3>{projects.filter(p => p.projectType === 'Completed Projects').length}</h3>
            <p>Completed</p>
          </div>
        </div>
        <div className="stat-card stat-upcoming">
          <div className="stat-icon">📅</div>
          <div className="stat-info">
            <h3>{projects.filter(p => p.projectType === 'Upcoming Projects').length}</h3>
            <p>Upcoming</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="filter-tabs">
          {['All', 'Ongoing Projects', 'Completed Projects', 'Upcoming Projects'].map(tab => (
            <button
              key={tab}
              className={`filter-tab ${filter === tab ? 'active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="search-box">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          fontSize: '18px',
          color: '#718096'
        }}>
          Loading projects...
        </div>
      )}

      {/* Projects Grid */}
      {!isLoading && filteredProjects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h2>No projects found</h2>
          <p>Start by adding your first construction project</p>
          <button className="empty-add-btn" onClick={handleAddNew}>
            Add Project
          </button>
        </div>
      ) : (
        !isLoading && (
          <div className="projects-grid">
            {filteredProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-card-header">
                  <span className={`project-status ${getStatusColor(project.projectType)}`}>
                    {project.projectType.replace(' Projects', '')}
                  </span>
                  <div className="project-actions">
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handleViewDetails(project.id)}
                      title="View Details"
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                    <button 
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteProject(project.id)}
                      title="Delete Project"
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                    <button
                      className="action-btn edit-btn"
                      onClick={() => navigate(`/project/${project.id}/edit`)}
                      title="Edit Project"
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>

                {project.images && project.images.length > 0 && (
                  <div className="project-image">
                    <img 
                      src={Array.isArray(project.images) && project.images[0] 
                        ? `${BASE_URL}${project.images[0]}`
                        : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3C/svg%3E'
                      }
                      alt={project.projectTitle}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3C/svg%3E';
                      }}
                    />
                    {project.images.length > 1 && (
                      <span className="image-count">+{project.images.length - 1}</span>
                    )}
                  </div>
                )}

                <div className="project-card-body">
                  <h3 className="project-title">{project.projectTitle}</h3>
                  <p className="project-client">
                    <span className="info-icon">👤</span>
                    {project.clientName}
                  </p>
                  <p className="project-location">
                    <span className="info-icon">📍</span>
                    {project.location}
                  </p>

                  <div className="project-details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Area</span>
                      <span className="detail-value">{project.area} sqft</span>
                    </div>
                    {project.floors && (
                      <div className="detail-item">
                        <span className="detail-label">Floors</span>
                        <span className="detail-value">{project.floors}</span>
                      </div>
                    )}
                  </div>

                  <div className="project-dates">
                    {project.projectType !== 'Completed Projects' && (
                      <div className="date-item">
                        <span className="date-label">Start</span>
                        <span className="date-value">{formatDate(project.startDate)}</span>
                      </div>
                    )}
                    <div className="date-item">
                      <span className="date-label">
                        {project.projectType === 'Completed Projects' ? 'Completed' : 'Expected'}
                      </span>
                      <span className="date-value">{formatDate(project.completionDate)}</span>
                    </div>
                  </div>

                  <div className="project-progress">
                    <div className="progress-header">
                      <span className="progress-text">Progress</span>
                      <span className="progress-percentage">{project.progress}%</span>
                    </div>
                    <div className="progress-bar-wrapper">
                      <div 
                        className={`progress-bar-fill ${project.projectType === 'Completed Projects' ? 'completed' : ''}`}
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default ProjectsDashboard;
